/**
 * The checkout (`/checkout`, web-sign-in-checkout D7, D8): the checkout piece
 * with each step's body; at phone width the app's booking pages (D12). An address that names nothing bookable shows the
 * studio's not-found page.
 */
import { useSearchParams } from 'react-router-dom';
import type { WebAppFlowContextResponse } from '@/api/types';

import { useWebStepBack } from '../hooks/useWebForms';
import { useWebFlowContext } from '../hooks/useWebFlow';
import { useWebHold } from '../hooks/useWebHold';
import { CheckoutView, useCheckout } from '../pieces/checkout';
import { FormsStep } from '../pieces/forms-step';
import { HoldClockLine, HoldClockWarning, useHoldClock } from '../pieces/hold-clock';
import { HoldRecoveryView, useHoldRecovery } from '../pieces/hold-recovery';
import { PaymentStep } from '../pieces/payment-step';
import { RegistrationStep } from '../pieces/registration-step';
import { ReviewStep } from '../pieces/review-step';
import { SignInStep } from '../pieces/sign-in-step';
import { PhoneCheckout } from '../phone/PhoneCheckout';
import { useAppScreens } from '../ui/useIsPhone';
import { usePageTitle } from '../ui/usePageTitle';
import { NotFoundPage } from './NotFoundPage';

interface LastStepProps {
  readonly flowToken: string;
  readonly flow: WebAppFlowContextResponse;
  readonly offeringPath: string | null;
  /** The summary asks for the number to call (service-by-phone D5): the step's button waits. */
  readonly waitingForNumber: boolean;
}

/** The payment step at the full price, as the app's (a deposit is refused there, row P2); Pick a new time steps back. */
function FlowPaymentStep({ flowToken, flow, offeringPath, waitingForNumber }: LastStepProps) {
  const stepBack = useWebStepBack(flowToken, flow, offeringPath);
  return (
    <PaymentStep
      entry={{ kind: 'flow', flowToken }}
      amount={flow.context.service?.price ?? 0}
      onPickNewTime={stepBack.back}
      waitingForNumber={waitingForNumber}
    />
  );
}

/** The review step; Go Back from a booking that did not go through steps back. */
function FlowReviewStep({ flowToken, flow, offeringPath, waitingForNumber }: LastStepProps) {
  const stepBack = useWebStepBack(flowToken, flow, offeringPath);
  return <ReviewStep flowToken={flowToken} flowContext={flow} onBack={stepBack.back} waitingForNumber={waitingForNumber} />;
}

export function CheckoutPage() {
  const checkout = useCheckout();
  const flowToken = useSearchParams()[0].get('flow');
  const flow = useWebFlowContext(flowToken).data;
  usePageTitle('Checkout');
  const hold = useWebHold(flowToken, flow, checkout.state.back?.href ?? null);
  const clock = useHoldClock(hold, checkout.state.kind);
  const recovery = useHoldRecovery(hold, flow?.flow.selectedSlot?.startsAt ?? flow?.flow.booking?.scheduledAt ?? null);
  const phone = useAppScreens();
  if (checkout.state.status === 'not-found') return <NotFoundPage />;
  if (checkout.state.status === 'loading') return null;
  // Below 768px the checkout is the app's booking pages, one step per screen (design D12).
  if (phone) return <PhoneCheckout checkout={checkout} flowToken={flowToken} flow={flow} clock={clock} recovery={recovery} holdAttempt={hold.attempt} />;

  const { back, summary } = checkout.state;
  const offering = back && summary ? { name: summary.name, path: back.href } : null;
  const step = flow?.flow.currentStep;
  const forms =
    flowToken && flow && (step === 'REGISTRATION' || step === 'CUSTOM_FORMS') ? (
      step === 'REGISTRATION' ? (
        <RegistrationStep key="registration" flowToken={flowToken} flowContext={flow} offering={offering} />
      ) : (
        <FormsStep key="forms" flowToken={flowToken} flowContext={flow} offering={offering} />
      )
    ) : undefined;

  // The last step: Stripe for an offering paid online, else the review (pay at the session, free, a request).
  const service = flow?.context.service;
  const waitingForNumber = checkout.state.phoneCall?.asking ?? false;
  const last =
    flowToken && flow && service ? (
      !service.requiresApproval && (service.paymentRequirement === 'FULL_PAYMENT' || service.paymentRequirement === 'DEPOSIT') ? (
        <FlowPaymentStep key={hold.attempt} flowToken={flowToken} flow={flow} offeringPath={back?.href ?? null} waitingForNumber={waitingForNumber} />
      ) : (
        <FlowReviewStep flowToken={flowToken} flow={flow} offeringPath={back?.href ?? null} waitingForNumber={waitingForNumber} />
      )
    ) : undefined;

  return (
    <CheckoutView
      {...checkout}
      bodies={{ signIn: <SignInStep />, forms, payment: last, request: last }}
      hold={{ line: <HoldClockLine {...clock} />, warning: <HoldClockWarning {...clock} inline />, recovery: <HoldRecoveryView {...recovery} /> }}
    />
  );
}
